import { Autocomplete, DialogContent, DialogTitle, Dialog as MuiDialog, TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Loader } from 'components';
import { SelectedLocationContext } from 'contexts/selected-location';
import dayjs, { Dayjs } from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { useInvoiceApi } from 'hooks/use-invoice-api';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Customer } from 'types';

dayjs.extend(utc);

export type GenerateInvoicesDialogMode = 'generate' | 'preview';

type GenerateInvoicesDialogProps = {
  mode: GenerateInvoicesDialogMode;
  open: boolean;
  close: () => void;
  customers: Customer[];
  onSuccess?: () => void;
};

/** A customer choice; `id: null` is All Customers. */
type CustomerOption = { id: number | null; label: string };

const ALL_CUSTOMERS: CustomerOption = { id: null, label: 'All Customers' };
const isAll = (option: CustomerOption) => option.id === null;

/* All Customers and individual customers exclude each other: picking All replaces the selection,
 * picking a customer drops All. Removals pass through, so the selection can become empty */
const exclusiveSelection = (previous: CustomerOption[], next: CustomerOption[]): CustomerOption[] => {
  const added = next.filter(option => !previous.some(p => p.id === option.id));
  if (added.some(isAll)) {
    return [ALL_CUSTOMERS];
  }
  if (added.length) {
    return next.filter(option => !isAll(option));
  }
  return next;
};

/* The dates are stored as UTC and the backend reads their date part as wall-clock time at the
 * location. Preview starts from the user's own calendar date, because dayjs.utc() is already
 * tomorrow for a US user in the evening */
export const defaultDates = (mode: GenerateInvoicesDialogMode, now: Dayjs = dayjs()): [Dayjs, Dayjs] => {
  if (mode === 'preview') {
    const today = dayjs.utc(now.format('YYYY-MM-DD'));
    return [today.startOf('month'), today.endOf('day')];
  }
  const previousMonth = dayjs.utc().subtract(1, 'month');
  return [previousMonth.startOf('month'), previousMonth.endOf('month')];
};

const SELECT_A_CUSTOMER = 'Select at least one customer, or All Customers.';

export const GenerateInvoicesDialog: FC<GenerateInvoicesDialogProps> = ({
  mode,
  open,
  close,
  customers,
  onSuccess
}) => {
  // STATE
  const [startDate, setStartDate] = useState(() => defaultDates(mode)[0]);
  const [endDate, setEndDate] = useState(() => defaultDates(mode)[1]);
  const [splitLoaded, setSplitLoaded] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerOption[]>([ALL_CUSTOMERS]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const customerInputRef = useRef<HTMLInputElement>(null);
  const { selectedLocation } = useContext(SelectedLocationContext);

  const { generateInvoices, downloadChargesPreview } = useInvoiceApi();

  const customerOptions = useMemo<CustomerOption[]>(
    () => [ALL_CUSTOMERS, ...customers.map(c => ({ id: c.id, label: c.name }))],
    [customers]
  );

  // CALLBACKS
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    if (submitLoading || selectedLocation === null) {
      return;
    }
    if (!selectedCustomers.length) {
      customerInputRef.current?.reportValidity();
      return;
    }
    // All Customers sends no customer parameter; otherwise every chosen id, even a single one.
    const customerIds = selectedCustomers.some(isAll) ? undefined : selectedCustomers.map(c => c.id as number);
    setSubmitLoading(true);
    if (mode === 'preview') {
      downloadChargesPreview({
        locationId: selectedLocation.id,
        from: startDate.toDate(),
        to: endDate.toDate(),
        customerIds
      })
        .then(downloaded => {
          // On failure the error is shown and the dialog stays open with the user's inputs.
          if (downloaded) {
            close();
          }
        })
        .finally(() => setSubmitLoading(false));
      return;
    }
    generateInvoices({
      customerIds,
      from: startDate.toDate(),
      to: endDate.toDate(),
      locationId: selectedLocation.id,
      splitLoaded
    })
      .then(invoices => {
        if (onSuccess && !!invoices) {
          onSuccess();
        }
        close();
      })
      .finally(() => setSubmitLoading(false));
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    close();
  };

  // EFFECTS
  // The form's native check blocks submit while nothing is selected.
  useEffect(() => {
    customerInputRef.current?.setCustomValidity(selectedCustomers.length ? '' : SELECT_A_CUSTOMER);
  }, [selectedCustomers, open]);

  return (
    <MuiDialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          background: 'white',
          overflowY: 'unset'
        }
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      }}
    >
      <DialogTitle>{mode === 'preview' ? 'Generate Preview' : 'Generate Invoices'}</DialogTitle>
      <DialogContent sx={{ overflowY: 'unset' }}>
        <form ref={formRef} onSubmit={handleSubmit} data-cy="generate-invoices-form">
          <div className="grid-2">
            <div>
              <label>Start Date</label>
              <div className="mt-2">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    sx={{ backgroundColor: 'var(--white)' }}
                    slotProps={{
                      textField: { margin: 'none', required: true, size: 'small', className: 'w-100' }
                    }}
                    value={startDate}
                    onChange={date => setStartDate(date!)}
                  />
                </LocalizationProvider>
              </div>
            </div>
            <div>
              <label>End Date</label>
              <div className="mt-2">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    sx={{ backgroundColor: 'var(--white)' }}
                    slotProps={{
                      textField: { margin: 'none', required: true, size: 'small', className: 'w-100' }
                    }}
                    value={endDate}
                    onChange={date => setEndDate(date!)}
                  />
                </LocalizationProvider>
              </div>
            </div>
            <div className="col-span-2">
              <label>Customer</label>
              <div className="mt-2">
                <Autocomplete<CustomerOption, true>
                  multiple
                  disableCloseOnSelect
                  limitTags={3}
                  size="small"
                  options={customerOptions}
                  value={selectedCustomers}
                  onChange={(_, next) => setSelectedCustomers(previous => exclusiveSelection(previous, next))}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={option => option.label}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id ?? 'all'}>
                      {option.label}
                    </li>
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      inputRef={customerInputRef}
                      sx={{ backgroundColor: 'var(--white)' }}
                      inputProps={{ ...params.inputProps, 'data-cy': 'generate-invoices-account-input' }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-4">
            {mode === 'generate' ? (
              <div className="d-flex align-items-center">
                <input type="checkbox" checked={splitLoaded} onChange={() => setSplitLoaded(!splitLoaded)} />
                <label className="ml-2">Split Loaded/Empty Containers</label>
              </div>
            ) : (
              <div />
            )}
            <div className="d-flex g-3">
              <button type="button" className="btn-outline" onClick={handleCancel}>
                Cancel
              </button>
              <Loader loading={submitLoading} scale="50%">
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {mode === 'preview' ? 'Download CSV' : 'Submit'}
                </button>
              </Loader>
            </div>
          </div>
        </form>
      </DialogContent>
    </MuiDialog>
  );
};
